import { useTranslation } from 'react-i18next';
import './ClassProgress.scss';
import '../../OrganizationTables.scss';
import { Students } from 'types/organization/students';
import {
  Loader,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@trackman/web-shared-components';
import { useMemo, useState } from 'react';
import { LessonProgress } from 'types/organization/classes';
import { ReactComponent as ArrowIcon } from 'images/icons/Icon_Left.svg';
import { TestIframe } from 'pages/LearningHub/LearningModule/TypeSpecificComponents/TestIframe/TestIframe';
import { useServices } from 'index';
import { useQuery } from '@tanstack/react-query';

type Props = {
  moduleProgress: Students;
  classId: string;
};

export const ClassProgress = ({ moduleProgress, classId }: Props) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isModalOn, setIsModalOn] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState('');

  const progressList: LessonProgress[] = useMemo(
    () =>
      moduleProgress
        ? moduleProgress
            .map((progress) => progress.progressList)
            .flat()
            .filter((item): item is LessonProgress => item !== undefined)
        : [],
    [moduleProgress]
  );

  const testsGroupedById = useMemo(() => {
    return progressList.reduce((grouped: Record<string, LessonProgress[]>, item) => {
      if (!grouped[item.lessonId]) {
        grouped[item.lessonId] = [];
      }
      grouped[item.lessonId].push(item);
      return grouped;
    }, {});
  }, [progressList]);

  const classProgressSummary = useMemo(() => {
    if (!testsGroupedById) {
      return [];
    }

    return Object.entries(testsGroupedById).map(([lessonId, progressList]) => {
      if (!progressList) {
        return undefined;
      }
      const totalProgress = progressList.reduce((sum, { percentage }) => sum + percentage, 0);
      const classProgress = Math.round((totalProgress / progressList.length) * 100);
      return {
        lessonId,
        lessonName: progressList[0].lessonName,
        classProgress,
        status:
          classProgress === 100
            ? t('Completed', { ns: 'organizationPage' })
            : classProgress === 0
              ? t('NotStartedYet', { ns: 'organizationPage' })
              : t('Started', { ns: 'organizationPage' }),
      };
    });
  }, [testsGroupedById]);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleArea9ProgressClick = (testId: string) => {
    setSelectedTestId(testId);
    setIsModalOn(true);
  };

  const handleCloseModal = () => {
    setIsModalOn(false);
    setSelectedTestId('');
  };

  return (
    <div className='organization-table-card-wrapper class-progress-table-wrapper'>
      <Table className='class-progress-table tmu-table'>
        <TableHead>
          <TableRow>
            <TableCell>{t('Test', { ns: 'organizationPage' })}</TableCell>
            <TableCell className='student-name'>{t('StudentsProgress', { ns: 'general' })}</TableCell>
            <TableCell>{t('Status', { ns: 'organizationPage' })}</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {classProgressSummary?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((entry) => (
            <TableRow key={entry?.lessonId}>
              <TableCell>{entry?.lessonName}</TableCell>
              <TableCell>
                <div className={`class-progress-info ${entry?.status.toLowerCase().split(' ').join('-')}`}>
                  <div className='class-progressbar'>
                    <span style={{ width: `${entry?.classProgress}%` }}></span>
                  </div>
                  <div className='class-progress-percentage'>{entry?.classProgress}%</div>
                </div>
              </TableCell>
              <TableCell>
                <div className={`class-progress-status ${entry?.status.toLowerCase().split(' ').join('-')}`}>{entry?.status}</div>
              </TableCell>
              <TableCell>
                <div className='area9-progress-link' onClick={() => handleArea9ProgressClick(entry?.lessonId || '')}>
                  <ArrowIcon />
                </div>
              </TableCell>
            </TableRow>
          ))}
          <ProgressModal classId={classId} testId={selectedTestId} isModalOn={isModalOn} setIsModalOn={handleCloseModal} />
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
              labelRowsPerPage={t('RowsPerPage', { ns: 'general' })}
              count={moduleProgress.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
};

const ProgressModal = ({ classId, testId, isModalOn, setIsModalOn }) => {
  const { t } = useTranslation();
  const area9ProgressService = useServices().area9ProgressService;

  const { data: area9Progress, isPending: isArea9ProgressPending } = useQuery({
    queryKey: ['area9Progress', `${classId}-${testId}`],
    queryFn: () => area9ProgressService.showArea9Progress(classId, testId),
    refetchOnWindowFocus: false,
    enabled: isModalOn && !!testId && !!classId,
    gcTime: 0,
  });

  return (
    <Modal
      title={t('ClassProgress', { ns: 'organizationPage' })}
      open={isModalOn}
      onClose={() => setIsModalOn(false)}
      className='class-progress-modal'
    >
      <div className='modal-body'>
        {!isArea9ProgressPending && area9Progress ? (
          <TestIframe area9LaunchUrl={area9Progress?.area9LaunchUrl} lessonParams={area9Progress?.parameters} />
        ) : (
          <div className='new-loader-wrapper'>
            <Loader size='large' />
          </div>
        )}
      </div>
    </Modal>
  );
};
